import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { IdentityServiceBase } from '@seahorse/auth';
import { SeahorseMenuItem } from '@seahorse/layout';
import {
  ShMenuComponent,
  ShMenuItem,
  ShTooltipDirective,
} from '@seahorse/components';
import { HasFeatureDirective } from '@seahorse/ui';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

const STORAGE_KEY = 'shipm8.rail-expanded';

/** Reads the persisted rail expanded/collapsed state, defaulting to expanded. */
function readRailExpanded(): boolean {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === null ? true : value === 'true';
  } catch {
    return true;
  }
}

@Component({
  selector: 'sh-app-rail',
  templateUrl: './app-rail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    HasFeatureDirective,
    ShTooltipDirective,
    ShMenuComponent,
  ],
})
export class AppRailComponent implements OnInit, OnDestroy {
  menu = input.required<SeahorseMenuItem[]>();

  private readonly _identityService = inject(IdentityServiceBase);
  private readonly _router = inject(Router);
  private readonly _subscriptions = new Subscription();

  readonly expanded = signal(readRailExpanded());

  /** The current URL, so the active state re-renders on navigation (OnPush). */
  private readonly _url = signal('');

  // Items whose inline group (expanded mode) is open. Seeded on
  // navigation with whichever group contains the active route, but the
  // user can also open/close a group by hand.
  private readonly _openGroups = new Set<SeahorseMenuItem>();
  private readonly _flyoutItems = new WeakMap<SeahorseMenuItem, ShMenuItem[]>();

  topItems: SeahorseMenuItem[] = [];
  bottomItems: SeahorseMenuItem[] = [];

  ngOnInit(): void {
    this._url.set(this._router.url);
    this._loadItems();
    this._syncOpenGroupsWithRoute();

    this._subscriptions.add(
      this._router.events
        .pipe(filter((e) => e instanceof NavigationEnd))
        .subscribe(() => {
          this._url.set(this._router.url);
          this._syncOpenGroupsWithRoute();
        })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  toggleExpanded(): void {
    const next = !this.expanded();
    this.expanded.set(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // Storage unavailable: the state just won't survive a reload.
    }
  }

  isGroupOpen(item: SeahorseMenuItem): boolean {
    return this._openGroups.has(item);
  }

  toggleGroup(item: SeahorseMenuItem): void {
    if (this._openGroups.has(item)) {
      this._openGroups.delete(item);
    } else {
      this._openGroups.add(item);
    }
  }

  /**
   * The item's route or anything below it. A route ending in `/overview`
   * stands for its whole section: `nautical/ship/overview` is also active on
   * `nautical/ship/details/12`, so the rail keeps its item on detail pages.
   */
  isRouteActive(route?: string): boolean {
    if (!route) return false;
    this._url(); // re-evaluated on every navigation (OnPush)
    const options = {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    } as const;
    if (this._router.isActive(this._absolute(route), options)) return true;
    const section = route.replace(/\/overview$/, '');
    return (
      section !== route &&
      this._router.isActive(this._absolute(section), options)
    );
  }

  isGroupActive(item: SeahorseMenuItem): boolean {
    return !!item.items?.some(
      (child) => this.isRouteActive(child.route) || this.isGroupActive(child)
    );
  }

  fontAwesomeClass(item: SeahorseMenuItem): string {
    return item.icon ? `fa-solid ${item.icon}` : '';
  }

  navigate(route?: string): void {
    if (!route) return;
    this._router.navigate([this._absolute(route)]);
  }

  // The flyout menu of a collapsed item's children: labels stay translation
  // keys (sh-menu translates them when it opens, so they reflect the current
  // language). Always an sh-menu, even for a single level of children: it
  // renders a flat popup for those and a tiered one for nested groups.
  // Built once per item: a getter returning a new array every change
  // detection would hand the menu a new model each time.
  flyoutItems(item: SeahorseMenuItem): ShMenuItem[] {
    let items = this._flyoutItems.get(item);
    if (!items) {
      items = this._toMenuItems(item.items ?? []);
      this._flyoutItems.set(item, items);
    }
    return items;
  }

  private _toMenuItems(items: SeahorseMenuItem[]): ShMenuItem[] {
    return this._filterItems(items).map((item) => ({
      label: item.label,
      icon: item.icon ? `fa-solid ${item.icon}` : undefined,
      items: item.items?.length ? this._toMenuItems(item.items) : undefined,
      command: item.route ? () => this.navigate(item.route) : undefined,
    }));
  }

  private _loadItems(): void {
    const filtered = this._filterItems(this.menu());

    this.topItems = filtered
      .filter((item) => !item.placement || item.placement === 'top')
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    this.bottomItems = filtered
      .filter((item) => item.placement === 'bottom')
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  // Permission filtering mirrors the existing super-user sidebar
  // (SidebarComponent.loadNavigation): a missing `permissions` means
  // "always visible", otherwise IdentityServiceBase.hasPermission decides.
  // Feature filtering reuses *shHasFeature in the template instead, so it
  // isn't duplicated here.
  private _filterItems(items: SeahorseMenuItem[]): SeahorseMenuItem[] {
    return items.filter(
      (item) =>
        !item.permissions ||
        this._identityService.hasPermission(item.permissions)
    );
  }

  private _syncOpenGroupsWithRoute(): void {
    for (const item of [...this.topItems, ...this.bottomItems]) {
      if (item.items?.length && this.isGroupActive(item)) {
        this._openGroups.add(item);
      }
    }
  }

  private _absolute(route: string): string {
    return route.startsWith('/') ? route : `/${route}`;
  }
}
